import {
  Button,
  EmailField,
  FormFeedback,
  InputAdornment,
  PasswordField,
  TextField,
  User,
} from '@sparemin/blockhead';
import { noop, over } from 'lodash-es';
import React from 'react';
import AuthSpacer from '../AuthSpacer';
import LegalText from './LegalText';
import useSignUpForm, { UseSignUpFormConfig } from './useSignUpForm';

export interface SignUpFormProps
  extends Pick<UseSignUpFormConfig, 'onAuthenticateSuccess' | 'onError'>,
    Pick<React.HTMLProps<HTMLFormElement>, 'onSubmit'> {
  className?: string;
  isDisabled?: boolean;
}

function SignUpForm({
  className,
  isDisabled,
  onAuthenticateSuccess,
  onError,
  onSubmit: onSubmitProp = noop,
}: SignUpFormProps): React.ReactElement | null {
  const {
    hasInputError,
    isLoading,
    onSubmit: handleSubmit,
    register,
    formState: { errors },
    validationState,
  } = useSignUpForm({ onAuthenticateSuccess, onError });

  const disabled = isDisabled || isLoading;

  return (
    <AuthSpacer
      as="form"
      className={className}
      onSubmit={over([onSubmitProp, handleSubmit])}
    >
      <TextField
        aria-label="name field"
        autoComplete="name"
        errorMessage={errors.name?.message}
        fluid
        isDisabled={disabled}
        placeholder="Name"
        startAdornment={
          <InputAdornment position="start">
            <User />
          </InputAdornment>
        }
        optional
        validationState={validationState.name}
        {...register('name')}
      />
      <EmailField
        aria-label="email field"
        autoComplete="email"
        errorMessage={errors.email?.message}
        fluid
        isDisabled={disabled}
        placeholder="Email"
        validationState={validationState.email}
        {...register('email')}
      />
      <PasswordField
        autoComplete="new-password"
        errorMessage={errors.password?.message}
        fluid
        isDisabled={disabled}
        validationState={validationState.password}
        {...register('password')}
      />
      <LegalText />
      {errors?.unregisteredError && (
        <FormFeedback>{errors.unregisteredError?.message}</FormFeedback>
      )}
      <Button isDisabled={hasInputError || disabled} fluid type="submit">
        sign up
      </Button>
    </AuthSpacer>
  );
}

export default SignUpForm;
